import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import { Button } from '@crm/shared/ui';
import { Spinner } from '@crm/shared/ui/Spinner';
import { DealDetailsContent } from '@crm/widgets/deal-details-content';
import { dashboardActions } from 'store/slices/dashboardSlice/dashboardSlice';

import { useGetDealDetailsQuery } from './api/useGetDealDetailsQuery';
import { DealProjectDashboard } from './ui/DealProjectDashboard';
import { EmptyDashboard } from './ui/EmptyDashboard';
import { Header } from './ui/Header';

export const DealDetailsPage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { id } = useParams();
    const dealId = Number(id);
    const [shouldOpenProjectTeamModal, setShouldOpenProjectTeamModal] = useState(false);

    const {
        isError,
        isFetching,
        isPending,
        refetch,
        data: dealDetailsData,
    } = useGetDealDetailsQuery(dealId);

    useEffect(() => {
        if (!shouldOpenProjectTeamModal || !dealDetailsData?.projectData) {
            return;
        }

        dispatch(dashboardActions.onModalMode('teamProject'));
        setShouldOpenProjectTeamModal(false);
    }, [dealDetailsData?.projectData, dispatch, shouldOpenProjectTeamModal]);

    if (!id || !Number.isFinite(dealId)) {
        return null;
    }

    if (isPending) {
        return (
            <div className="flex w-full items-center justify-center">
                <Spinner className="size-8" />
            </div>
        );
    }

    if (isError) {
        return (
            <div className="flex flex-col items-start gap-3">
                <div>{t('crm.dealDetailsPage.errors.load')}</div>
                <Button disabled={isFetching} onClick={() => refetch()} type="button">
                    {isFetching ? t('loading.text') : t('button.retry')}
                </Button>
            </div>
        );
    }

    if (!dealDetailsData) {
        return <div>{t('crm.dealDetailsPage.states.noData')}</div>;
    }

    return (
        <div className="-ml-3 flex h-full min-h-0 w-[calc(100%+0.75rem)] flex-col gap-6 overflow-y-auto pl-3">
            <Header dealDetailsData={dealDetailsData} />
            <DealDetailsContent dealDetailsData={dealDetailsData} />
            {dealDetailsData?.projectData ? (
                <DealProjectDashboard projectData={dealDetailsData.projectData} />
            ) : (
                <EmptyDashboard
                    action={dealDetailsData.projectAccessData.action}
                    isAllowed={dealDetailsData.projectAccessData.isAllowed}
                    dealId={dealId}
                    onProjectCreated={() => setShouldOpenProjectTeamModal(true)}
                />
            )}
        </div>
    );
};
